import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";

import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { EM_DASH } from "@/lib/utils";

import { DEVICE_LOOKUP_KEY, fetchDeviceLookup, type DeviceEpisode } from "./devicesApi";
import { qualityNcrDeviceWorkQueryKey, qualityNcrLabel, saveQualityNcrDeviceWork } from "./qualityEventNcrApi";
import type { NcrDeviceWork } from "./qualityRcaApi";
import { QualityRcaDeviceModal } from "./QualityRcaDeviceModal";
import { fetchPcbaSkus } from "./rmaApi";
import type { RcaDeviceAutomaticLog } from "./QualityRcaWorksheet";
import { presentNcrDeviceWork, rcaDeviceKey, type RcaDeviceWork, type RcaFormState } from "./rcaForm";

export interface RcaWorksheetDevice {
  serial: string;
  /** The device's NCR. Null when no quality incident exists for this serial yet. */
  qualityIncidentId: number | null;
}

export function QualityRcaDevicesCard({
  devices,
  deviceLogs,
  pcbaLocationBySerial,
  ncrDeviceWork,
  form,
  rcaId,
  writesAllowed,
  onNcrChanged,
}: {
  devices: RcaWorksheetDevice[];
  deviceLogs: Record<string, RcaDeviceAutomaticLog>;
  pcbaLocationBySerial: Record<string, number>;
  ncrDeviceWork: Record<number, NcrDeviceWork>;
  /** Legacy device entries under form.devices seed the NCR worksheet until it is saved once. */
  form: RcaFormState;
  rcaId: number;
  writesAllowed: boolean;
  onNcrChanged: () => Promise<void> | void;
}) {
  const queryClient = useQueryClient();
  const [openSerial, setOpenSerial] = useState<string | null>(null);
  const [workBySerial, setWorkBySerial] = useState<Record<string, RcaDeviceWork>>({});
  const lookups = useQueries({
    queries: devices.map((device) => ({
      queryKey: [DEVICE_LOOKUP_KEY, device.serial],
      queryFn: () => fetchDeviceLookup(device.serial),
      retry: false,
      enabled: device.serial.trim() !== "",
    })),
  });
  const openDevice = openSerial == null ? null : devices.find((device) => rcaDeviceKey(device.serial) === rcaDeviceKey(openSerial)) ?? null;

  const workFor = (device: RcaWorksheetDevice): RcaDeviceWork => {
    const key = rcaDeviceKey(device.serial);
    const local = workBySerial[key];
    if (local != null) return local;
    const legacy = form.devices[key] ?? null;
    const stored = device.qualityIncidentId == null ? null : ncrDeviceWork[device.qualityIncidentId]?.work ?? null;
    return presentNcrDeviceWork(stored, legacy);
  };

  const saveBoardSku = useMutation({
    mutationFn: async ({ device, sku }: { device: RcaWorksheetDevice; sku: string }) => {
      if (device.qualityIncidentId == null) return null;
      const next = { ...workFor(device), boardSku: sku };
      setWorkBySerial((current) => ({ ...current, [rcaDeviceKey(device.serial)]: next }));
      const saved = await saveQualityNcrDeviceWork(device.qualityIncidentId, next);
      queryClient.setQueryData(qualityNcrDeviceWorkQueryKey(device.qualityIncidentId), saved);
      return saved;
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="One row per device on the linked RMAs and NCRs. Board SKU, battery, diagnostics, log windows, and photos are stored on each device's NCR.">
          Devices
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-3">
        {devices.length === 0 ? (
          <p className="text-sm text-slate-600">No devices are linked to this RCA yet. Link an RMA or an NCR.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="px-2 py-2 font-medium">Serial</th>
                  <th className="px-2 py-2 font-medium">NCR</th>
                  <th className="px-2 py-2 font-medium">SKU</th>
                  <th className="px-2 py-2 font-medium">Firmware</th>
                  <th className="px-2 py-2 font-medium">IMEI</th>
                  <th className="px-2 py-2 font-medium">Board SKU</th>
                  <th className="px-2 py-2 font-medium">Manufactured</th>
                  <th className="px-2 py-2 font-medium">PCBA</th>
                  <th className="px-2 py-2 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device, index) => {
                  const serial = device.serial;
                  const episode = activeEpisode(lookups[index]?.data?.episodes);
                  const loading = lookups[index]?.isLoading === true;
                  const work = workFor(device);
                  const suggestedBoard = episode?.pcbaBuildOrderSku?.trim() || "";
                  const boardSku = work.boardSku.trim() !== "" ? work.boardSku : suggestedBoard;
                  return (
                    <tr key={serial} className="border-b border-slate-100 align-top">
                      <td className="px-2 py-2">
                        <DetailLinkWithEntityPeek
                          peek={{
                            kind: "device",
                            context: {
                              serial,
                              model: episode?.finishedSku,
                              firmwareVersion: episode?.firmwareVersion,
                            },
                          }}
                          trigger={
                            <Link
                              className="font-mono text-sky-800 hover:underline"
                              to={`/quality/devices?label=${encodeURIComponent(serial)}`}
                            >
                              {serial}
                            </Link>
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        {device.qualityIncidentId == null ? (
                          <span className="text-slate-500">None</span>
                        ) : (
                          <Link className="text-sky-800 hover:underline" to={`/quality/ncrs/${device.qualityIncidentId}`}>
                            {qualityNcrLabel(device.qualityIncidentId)}
                          </Link>
                        )}
                      </td>
                      <td className="px-2 py-2">{loading ? "Loading" : episode?.finishedSku || EM_DASH}</td>
                      <td className="px-2 py-2">{loading ? "" : episode?.firmwareVersion || EM_DASH}</td>
                      <td className="px-2 py-2 font-mono text-xs">{loading ? "" : episode?.imei || EM_DASH}</td>
                      <td className="px-2 py-2">
                        <BoardSkuPicker
                          serial={serial}
                          boardSku={loading ? "" : boardSku}
                          disabled={!writesAllowed || device.qualityIncidentId == null}
                          onSave={(sku) => saveBoardSku.mutate({ device, sku })}
                        />
                      </td>
                      <td className="px-2 py-2">{loading ? "" : episode?.dateAssembled?.slice(0, 10) || EM_DASH}</td>
                      <td className="px-2 py-2">
                        {!loading ? (
                          <PcbaSerialCell serial={episode?.pcbaSerial} locationId={pcbaLocationBySerial[rcaDeviceKey(serial)]} />
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        <Button type="button" variant="secondary" size="sm" onClick={() => setOpenSerial(serial)}>
                          Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {saveBoardSku.isError ? (
          <p className="text-sm text-red-700">{(saveBoardSku.error as Error).message || "Could not save the board SKU."}</p>
        ) : null}
        <QualityRcaDeviceModal
          serial={openDevice?.serial ?? null}
          qualityIncidentId={openDevice?.qualityIncidentId ?? null}
          legacyWork={openDevice == null ? null : form.devices[rcaDeviceKey(openDevice.serial)] ?? null}
          automaticLog={openDevice == null ? null : deviceLogs[rcaDeviceKey(openDevice.serial)] ?? null}
          rcaId={rcaId}
          writesAllowed={writesAllowed}
          onClose={() => setOpenSerial(null)}
          onNcrCreated={onNcrChanged}
          onWorkSaved={(serial, work) => {
            setWorkBySerial((current) => ({ ...current, [rcaDeviceKey(serial)]: work }));
          }}
        />
      </CardBody>
    </Card>
  );
}

function PcbaSerialCell({ serial, locationId }: { serial: string | null | undefined; locationId: number | undefined }) {
  return (
    <>
      <div className="font-mono text-xs">{serial || EM_DASH}</div>
      {locationId != null && locationId > 0 ? (
        <div className="mt-1">
          <ManufacturingLocationChip locationId={locationId} />
        </div>
      ) : null}
    </>
  );
}

function activeEpisode(episodes: DeviceEpisode[] | undefined): DeviceEpisode | null {
  if (episodes == null || episodes.length === 0) return null;
  return episodes.find((episode) => episode.active) ?? episodes[0] ?? null;
}

function BoardSkuPicker({
  serial,
  boardSku,
  disabled,
  onSave,
}: {
  serial: string;
  boardSku: string;
  disabled: boolean;
  onSave: (sku: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [search]);
  const skusQuery = useQuery({
    queryKey: ["quality", "pcba-skus", debouncedSearch],
    queryFn: () => fetchPcbaSkus(debouncedSearch),
    enabled: open,
  });
  const options: SearchableSelectOption[] = (skusQuery.data?.rows ?? []).map((row) => ({
    value: row.sku,
    label: row.sku,
    description: row.description,
  }));

  if (!open) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50"
        aria-label={`Choose board SKU for ${serial}`}
        disabled={disabled}
        onClick={() => {
          setSearch(boardSku);
          setDebouncedSearch(boardSku.trim());
          setOpen(true);
        }}
      >
        <Pencil className="h-4 w-4 shrink-0" aria-hidden />
        {boardSku !== "" ? <span className="font-mono text-xs text-slate-900">{boardSku}</span> : null}
      </button>
    );
  }

  return (
    <div className="flex min-w-[16rem] items-start gap-2">
      <div className="min-w-0 flex-1">
        <SearchableSelect
          initialOpen
          value={boardSku}
          onValueChange={(option) => {
            onSave(option?.value ?? "");
            setOpen(false);
          }}
          options={options}
          searchValue={search}
          onSearchChange={setSearch}
          placeholder="Search PCBAs"
          searchPlaceholder="Search PCBAs"
          emptyMessage={skusQuery.isFetching ? "Searching" : "No matching PCBAs"}
          isLoading={skusQuery.isFetching}
          selectedLabelOverride={boardSku || null}
          panelMinWidth={320}
        />
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
        Close
      </Button>
    </div>
  );
}
